import {
    IKeyboardShortcut,
    ISubscriber,
    KeyboardShortcut,
    ShortcutType,
    TSubscriberCallback
} from "@utils/keyboardShortcutsManager/KeyboardShorcutsManager.utils";
import React from "react";
import * as deviceDetect from "react-device-detect";

import { TRecordType } from "../../global.types";
import { KeyName } from "../../keyName";

const shortcuts: Record<KeyboardShortcut, IKeyboardShortcut> = {
    [KeyboardShortcut.ALT_D]: {
        id: KeyboardShortcut.ALT_D,
        keys: [
            {
                char: "d",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_N]: {
        id: KeyboardShortcut.ALT_N,
        keys: [
            {
                char: "n",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_C]: {
        id: KeyboardShortcut.ALT_C,
        keys: [
            {
                char: "c",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_S]: {
        id: KeyboardShortcut.ALT_S,
        keys: [
            {
                char: "s",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_R]: {
        id: KeyboardShortcut.ALT_R,
        keys: [
            {
                char: "r",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_X]: {
        id: KeyboardShortcut.ALT_X,
        keys: [
            {
                char: "x",
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_1]: {
        id: KeyboardShortcut.ALT_SHIFT_1,
        keys: [
            {
                char: "1",
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_2]: {
        id: KeyboardShortcut.ALT_SHIFT_2,
        keys: [
            {
                char: "2",
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_3]: {
        id: KeyboardShortcut.ALT_SHIFT_3,
        keys: [
            {
                char: "3",
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_UP]: {
        id: KeyboardShortcut.ALT_UP,
        keys: [
            {
                char: KeyName.ArrowUp,
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_DOWN]: {
        id: KeyboardShortcut.ALT_DOWN,
        keys: [
            {
                char: KeyName.ArrowDown,
                altKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_UP]: {
        id: KeyboardShortcut.ALT_SHIFT_UP,
        keys: [
            {
                char: KeyName.ArrowUp,
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_DOWN]: {
        id: KeyboardShortcut.ALT_SHIFT_DOWN,
        keys: [
            {
                char: KeyName.ArrowDown,
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.CTRL_Q]: {
        id: KeyboardShortcut.CTRL_Q,
        keys: [
            {
                char: "q",
                ctrlKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_S]: {
        id: KeyboardShortcut.ALT_SHIFT_S,
        keys: [
            {
                char: "s",
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.ALT_SHIFT_X]: {
        id: KeyboardShortcut.ALT_SHIFT_X,
        keys: [
            {
                char: "x",
                altKey: true,
                shiftKey: true
            }
        ]
    },
    [KeyboardShortcut.SHIFT_SHIFT]: {
        id: KeyboardShortcut.SHIFT_SHIFT,
        keys: [
            {
                char: KeyName.Shift,
                shiftKey: true
            },
            {
                char: KeyName.Shift,
                shiftKey: true
            }
        ]
    }
};

interface ISubscriberIdentifier { id: number; }

class KeyboardShortcutsManager {
    #subscribers: (ISubscriber & ISubscriberIdentifier)[] = [];
    #sortedShortcuts: Map<KeyboardShortcut, TSubscriberCallback[]> = new Map();
    pressedKeys: string[] = [];
    #pressTimer: ReturnType<typeof setTimeout>;
    static PARTIAL_SHORTCUT = "partial";
    private counter = 0;

    // we start with global handling, but subscribe could be provided DOM ref,
    // to only handle key presses from that DOM subtree
    public subscribe = (subscriber: ISubscriber): () => void => {
        const id = ++this.counter;
        this.#subscribers.push({ ...subscriber, id });
        this.sortShortcuts();

        if (this.#subscribers.length === 1) {
            this.startListening();
        }

        return /*unsubscribe*/() => {
            const index = this.#subscribers.findIndex(s => s.id === id);
            if (index === -1) {
                console.warn(`Subscriber with id ${id} not found.`);
                return;
            }
            this.#subscribers.splice(index, 1);
            this.sortShortcuts();

            if (this.#subscribers.length === 0) {
                this.stopListening();
            }
        };
    };

    /**
     * Process all subscribers and prepares shortcuts map sorted by priority.
     */
    private sortShortcuts = (): void => {
        const tmp: TRecordType<({ callback: TSubscriberCallback, priority: ShortcutType } & ISubscriberIdentifier)[]> = {};
        this.#subscribers.forEach(s => {
            s.shortcuts.forEach(shortcut => {
                if (!shortcuts[shortcut.id]) {
                    console.warn(`Shortcut ${shortcut.id} is not defined.`);
                    return;
                }
                if (!tmp[shortcut.id]) {
                    tmp[shortcut.id] = [];
                }
                tmp[shortcut.id].push({
                    id: s.id,
                    callback: s.callback,
                    priority: shortcut.priority ?? ShortcutType.Normal
                });
            });
        });

        this.#sortedShortcuts = new Map<KeyboardShortcut, TSubscriberCallback[]>();
        Object.keys(tmp).forEach((shortcutId) => {
            // sort by priority, so that the primary shortcuts are first
            tmp[shortcutId].sort((a, b) => a.priority === b.priority ? b.id - a.id : a.priority - b.priority);

            this.#sortedShortcuts.set(shortcutId as KeyboardShortcut, tmp[shortcutId].map(item => item.callback));
        });
    }

    private startListening = (): void => {
        document.body.addEventListener("keydown", this.handleKeyDown);
    };

    private stopListening = (): void => {
        document.body.removeEventListener("keydown", this.handleKeyDown);
    };

    private notifySubscribers = async (shortcut: KeyboardShortcut, event: KeyboardEvent): Promise<void> => {
        // iterate from the back so that the later subscribers are notified first
        const sortedCallbacks = this.#sortedShortcuts.get(shortcut);
        for (let i = 0; i < sortedCallbacks?.length; i++) {
            const callback = sortedCallbacks[i];
            const eventHandled = await callback.call(null, shortcut, event);

            if (eventHandled) {
                // if the event was handled, we stop propagation
                return;
            }
        }
    };

    public isEventShortcut = (event: KeyboardEvent | React.KeyboardEvent, shortcutId: KeyboardShortcut): boolean | string => {
        const shortcut = shortcuts[shortcutId];
        const index = this.pressedKeys.length;
        const currentShortcutKey = shortcut.keys[index];

        if (!currentShortcutKey) {
            return false;
        }

        const currentShortcutChar = currentShortcutKey.char.toLowerCase();
        let condition: boolean = !!currentShortcutKey.ctrlKey === event.ctrlKey && !!currentShortcutKey.altKey === event.altKey && !!currentShortcutKey.shiftKey === event.shiftKey;
        const pressedKey = event.key.toLowerCase();

        // on Mac, alt by default works as altGr,
        // meaning for some keyboard layouts, different character is triggered than the on user pressed (like "<" or accent characters)
        // and the event.key returns "Dead" or some special value instead of value of the pressed key.
        // ==> use event.code which always returns value of the pressed key.
        // !warning! the returned key always corresponds to the key on the keyboard and doesn't care about the system keyboard layout;
        // meaning, if the user use QUERTZ layout, event.code will still return y when y is pressed on keyboard.
        // https://stackoverflow.com/questions/61255621/trying-to-prevent-a-dead-key-from-being-typed-from-a-mac-keyboard
        // https://apple.stackexchange.com/questions/10761/how-to-disable-composite-keys-on-mac-os-x
        const pressedKeyCode = event.code?.toLowerCase();
        if (currentShortcutKey.altKey && deviceDetect.isMacOs && pressedKeyCode) {
            const prefix = ["key", "digit"].find(prefix => pressedKeyCode?.startsWith(prefix)) ?? "";
            const keyValue = `${prefix}${currentShortcutChar}`;

            condition = condition && (keyValue === pressedKeyCode || currentShortcutChar === pressedKey);
        } else {
            condition = condition && currentShortcutChar === pressedKey;
        }

        if (condition && index < shortcut.keys.length - 1) {
            return KeyboardShortcutsManager.PARTIAL_SHORTCUT;
        }

        return condition;
    };

    private getShortcutFromEvent = (event: KeyboardEvent): { shortcut: KeyboardShortcut, finished: boolean } => {
        if (!event.key) {
            return null;
        }

        for (const shortcut of Object.values(shortcuts)) {
            const res = this.isEventShortcut(event, shortcut.id);

            if (res === KeyboardShortcutsManager.PARTIAL_SHORTCUT) {
                return { shortcut: shortcut.id, finished: false };
            } else if (res) {
                return { shortcut: shortcut.id, finished: true };
            }
        }

        return null;
    };

    private finishPress = (): void => {
        this.pressedKeys = [];

        if (this.#pressTimer) {
            clearTimeout(this.#pressTimer);
            this.#pressTimer = null;
        }
    };

    private handleKeyDown = (event: KeyboardEvent): void => {
        if (event.repeat) {
            return;
        }

        const res = this.getShortcutFromEvent(event);

        if (res) {
            if (!res.finished) {
                this.pressedKeys.push(event.key.toLowerCase());
                this.#pressTimer = setTimeout(() => {
                    this.finishPress();
                }, 300);
            } else {
                this.finishPress();
                event.preventDefault();
                this.notifySubscribers(res.shortcut, event);
            }
        } else {
            this.finishPress();
        }
    };
}

const keyboardShortcutsManager = new KeyboardShortcutsManager();

export default keyboardShortcutsManager;